<template>
  <div class="page">
    <div fluid class="container-fluid main">
      <div class="fixed-header">
        <div class="container">
          <div class="row">
            <div class="col-12 col-md-6 col-lg-7"></div>
            <div class="col-md-12 col-lg-4">
              <Header/>
            </div>
          </div>
        </div>
      </div>
      <div class="content" :class="{ scrolled: scrolled }" @scroll="handleScroll">
        <div class="container" v-if="!isIE">
          <div class="row">
            <div class="col-12 col-md-6 col-lg-7 column-spacer"></div>
            <div class="col-md-12 col-lg-4 primary">
              <div class="form-container">
                <div class="message-box text-center" v-if="success">
                  Account created successfully.
                  <br/>
                  <router-link to="login" class="d-inline-block font-weight-bold mt-2">LOGIN</router-link>
                </div>
                
                <!-- Step 1: Registration Type Selection -->
                <div v-else-if="currentStep === 1">
                  <h2 class="heading">Register account</h2>
                  <div class="step-subheading">Register as</div>
                  <div class="selection-container">
                    <button 
                      type="button"
                      class="selection-card"
                      :class="{ active: registrationType === 'myself' }"
                      @click="selectRegistrationType('myself')"
                      aria-label="Register for myself"
                    >
                      <div class="selection-text">Myself</div>
                    </button>
                    <button 
                      type="button"
                      class="selection-card"
                      :class="{ active: registrationType === 'company' }"
                      @click="selectRegistrationType('company')"
                      aria-label="Register for my company"
                    >
                      <div class="selection-text">My company</div>
                    </button>
                  </div>
                  <div class="buttons single-back">
                    <button 
                      class="btn btn-back"
                      @click="goToLogin"
                    >
                      <img src="~@/assets/images/left-chevron.svg" alt="Back" class="chevron-icon" />
                      Back
                    </button>
                  </div>
                </div>

                <!-- Step 2: Registration Method Selection -->
                <div v-else-if="currentStep === 2">
                  <h2 class="heading">Register account</h2>
                  <div class="step-subheading">Register with</div>
                  <div class="selection-container">
                    <button 
                      type="button"
                      class="selection-card"
                      :class="{ active: registrationMethod === 'email' }"
                      @click="selectRegistrationMethod('email')"
                      aria-label="Register with email"
                    >
                      <div class="selection-text">Email</div>
                    </button>
                    <button 
                      type="button"
                      class="selection-card"
                      :class="{ active: registrationMethod === 'mobile' }"
                      @click="selectRegistrationMethod('mobile')"
                      aria-label="Register with mobile"
                    >
                      <div class="selection-text">Mobile</div>
                    </button>
                  </div>
                  <div class="buttons single-back">
                    <button 
                      class="btn btn-back"
                      @click="prevStep"
                    >
                      <img src="~@/assets/images/left-chevron.svg" alt="Back" class="chevron-icon" />
                      Back
                    </button>
                  </div>
                </div>

                <!-- Step 3: Registration Form -->
                <div v-else-if="currentStep === 3" class="step-3-form">
                  <h2 class="heading">Register account</h2>
                  <ErrorBox v-if="alert.message" variant="inline" :message="alert.message"
                  />
                  <form method="post" @submit.prevent="handleSubmit">
                    <div class="form-group" v-if="registrationMethod === 'email'">
                      <input
                        type="email"
                        v-model="emailAddress"
                        id="emailAddress"
                        name="emailAddress"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && !emailAddress }"
                        :disabled="inOtpPhase"
                      />
                      <label for="emailAddress">Email Address</label>
                      <ValidationFeedback 
                        :show="submitted && !emailAddress" 
                        message="Email address is required" 
                      />
                    </div>
                    
                    <div class="form-group" v-if="registrationMethod === 'mobile'">
                      <input
                        type="tel"
                        v-model="mobileNumber"
                        id="mobileNumber"
                        name="mobileNumber"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && !mobileNumber }"
                        :disabled="inOtpPhase"
                        placeholder="e.g. 0821234567"
                      />
                      <label for="mobileNumber">Mobile Number</label>
                      <ValidationFeedback 
                        :show="submitted && !mobileNumber" 
                        message="Mobile number is required" 
                      />
                    </div>

                    <div class="form-group">
                      <input
                        type="text"
                        pattern=".{5,15}"
                        maxlength="15"
                        title="Must be between 5 and 15 characters long"
                        v-model="idNumber"
                        id="idNumber"
                        name="idNumber"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && !idNumber }"
                        :disabled="inOtpPhase"
                      />
                      <label for="idNumber">{{ isIndividualType ? "ID/Passport Number/Netstar account number" : "Company Registration Number" }}</label>
                      <ValidationFeedback 
                        :show="submitted && !idNumber" 
                        :message="isIndividualType ? 'ID/Passport Number/Netstar Account Number is required' : 'Company Registration Number is required'" 
                      />
                    </div>
                    
                    <div class="form-group">
                      <div class="password-input-container">
                        <input
                          ref="passwordInput"
                          :type="showPassword ? 'text' : 'password'"
                          autocomplete="new-password"
                          pattern=".{8,}"
                          title="At least 8 characters"
                          v-model="password"
                          id="password"
                          name="password"
                          class="form-control"
                          :class="{ 'is-invalid': submitted && !password }"
                          :disabled="inOtpPhase"
                        />
                        <button 
                          type="button"
                          class="password-toggle-btn"
                          @click="togglePassword"
                        >
                          {{ showPassword ? 'Hide' : 'Show' }}
                        </button>
                      </div>
                      <label for="password">Password</label>
                      <ValidationFeedback 
                        :show="submitted && !password" 
                        message="Password is required" 
                      />
                    </div>
                    
                    <div class="form-group">
                      <div class="password-input-container">
                        <input
                          ref="confirmPasswordInput"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          autocomplete="new-password"
                          pattern=".{8,}"
                          title="At least 8 characters"
                          v-model="confirmPassword"
                          id="confirmPassword"
                          name="confirmPassword"
                          class="form-control"
                          :class="{
                            'is-invalid': submitted && (!confirmPassword || !passwordsMatch),
                          }"
                          :disabled="inOtpPhase"
                        />
                        <button 
                          type="button"
                          class="password-toggle-btn"
                          @click="toggleConfirmPassword"
                        >
                          {{ showConfirmPassword ? 'Hide' : 'Show' }}
                        </button>
                      </div>
                      <label for="confirmPassword">Confirm password</label>
                      <ValidationFeedback 
                        :show="submitted && (!confirmPassword || !passwordsMatch)" 
                        message="Passwords must match" 
                      />
                    </div>
                    
                    <div class="form-group" v-show="inOtpPhase">
                      <input
                        type="text"
                        pattern=".{5}"
                        maxlength="5"
                        title="Must be 5 characters"
                        v-model="otp"
                        id="otp"
                        name="otp"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && !otp }"
                      />
                      <label for="otp">OTP</label>
                      <ValidationFeedback 
                        :show="submitted && !otp" 
                        message="OTP is required" 
                      />
                      <div class="footer">
                        <div class="info-box">
                        <p class="text">Enter the One Time PIN (OTP) sent to your {{ registrationMethod === 'email' ? 'email address' : 'mobile number' }} 
                          (******{{ otpCellNumber }}) and then click on the 'CONFIRM' button to confirm 
                          your registration.
                        </p>
                      </div>
                      </div>
                    </div>
                    
                    <div class="form-group buttons">
                      <button 
                        class="btn btn-submit" 
                        :class="{ active: isFormValid }"
                        :disabled="processing"
                        type="submit"
                      >
                        <span v-show="!processing">{{ inOtpPhase ? "Confirm" : "Submit" }}</span>
                        <Spinner v-show="processing" />
                      </button>
                    </div>
                  </form>
                  <div class="buttons single-back">
                    <button 
                      class="btn btn-back"
                      @click="prevStep"
                    >
                      <img src="~@/assets/images/left-chevron.svg" alt="Back" class="chevron-icon" />
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <IncompatibleBrowser v-else/>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "~@/assets/scss/pages.scss";

.page {
  .content {
    > .container {
      .primary {
        .step-3-form.heading{
          margin-bottom: -20px;
        }
      
        .footer {
          margin-top: 24px;
          .text {
            color: $new-brand-color-2;
            font-size: 12px;
            margin: 0;
            line-height: 1.5em;
            font-weight: 600;
          }
        }

        .text-link {
          font-weight: bold;
        }

        .btn-register-as {
          background: #52c3df;
          font-weight: bold;
          min-width: 100%;
          font-size: 0.75rem;
          font-weight: 600;
          color: #fff;
          padding: 10px 30px;
          margin: 10px 0 20px 0;
          border-radius: 20px;
          border: none;

          &:hover {
            background: $brand-color-2;
            border-color: $brand-color-2;
          }

          &:disabled {
            opacity: 1;
          }
        }

      }
    }
  }
}

// Specific styles for RegisterPage
.step-subheading {
  font-size: 16px;
  color: $new-brand-color-1;
  margin: 24px 0 18px 0;
  font-weight: 600;
}
</style>

<script>
import {mapState, mapActions} from "vuex";
import {alertService, userService} from "@/services";
import Header from "@/pages/components/Header";
import IncompatibleBrowser from "@/components/IncompatibleBrowser";
import ErrorBox from "@/components/ErrorBox";
import ValidationFeedback from "@/components/ValidationFeedback";
import {helper} from "@/helper";

export default {
  components: {
    Header,
    IncompatibleBrowser,
    ErrorBox,
    ValidationFeedback,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.resetErrors();
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.resetErrors();
    next();
  },
  beforeRouteLeave(to, from, next) {
    this.resetErrors();
    next();
  },
  data() {
    return {
      currentStep: 1,
      registrationType: "", // 'myself' or 'company'
      registrationMethod: "", // 'email' or 'mobile'
      submitted: false,
      processing: false,
      success: false,
      subscriberType: "",
      subscriberTypeWith: "",
      idNumber: "",
      emailAddress: "",
      mobileNumber: "",
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      otp: "",
      token: "",
      otpCellNumber: "",
      passwordsMatch: false,
      inOtpPhase: false,
      scrolled: false,
      registrationError: "",
    };
  },
  computed: {
    ...mapState("account", ["status"]),
    ...mapState(["alert"]),
    isIndividualType() {
      return this.registrationType === "myself";
    },
    isIE() {
      return /*@cc_on!@*/ false || !!document.documentMode;
    },
    isFormValid() {
      const hasContactInfo = this.registrationMethod === 'email' ? this.emailAddress : this.mobileNumber;
      return hasContactInfo && this.idNumber && this.password && this.confirmPassword && this.passwordsMatch;
    },
  },
  methods: {
    ...mapActions("alert", {
      clearAlert: "clear",
    }),
    resetErrors() {
      this.registrationError = "";
      this.submitted = false;
      this.processing = false;
      this.clearAlert();
    },
    handleScroll(event) {
      this.scrolled = event.target.scrollTop > 5;
    },
    selectRegistrationType(type) {
      this.registrationType = type;
      this.subscriberType = type === 'myself' ? 'Individual' : 'Business';
      // Auto-advance to next step
      setTimeout(() => {
        this.nextStep();
      }, 300);
    },
    selectRegistrationMethod(method) {
      this.registrationMethod = method;
      // Auto-advance to next step
      setTimeout(() => {
        this.nextStep();
      }, 300);
    },
    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
      this.$nextTick(() => {
        this.$refs.passwordInput?.focus();
      });
    },
    toggleConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
      this.$nextTick(() => {
        this.$refs.confirmPasswordInput?.focus();
      });
    },
    goToLogin() {
      this.$router.push('/login');
    },
    handleSubmit(e) {
      this.clearAlert();
      this.submitted = true;
      this.registrationError = ""; // Clear any previous errors
      const { idNumber, emailAddress, mobileNumber, password, confirmPassword, passwordsMatch, otp, token, inOtpPhase, registrationMethod } = this;
      
      const username = registrationMethod === 'email' ? emailAddress : mobileNumber;

      if (idNumber && username && password && confirmPassword && passwordsMatch) {
        if (inOtpPhase && (!otp || !token)) {
          return;
        }
        this.processing = true;
        
        const registrationData = {
          idNumber: idNumber,
          username: username,
          password: password,
          otp: otp,
          token: token,
          origin: helper.getOriginInfo(),
        };
        
        userService
          .register(registrationData)
          .then((data) => {
            this.processing = false;
            this.submitted = false;
            this.token = data.Token;
            this.otpCellNumber = data.OTPCellNumber;
            this.inOtpPhase = !data.OTPVerified;
            this.success = data.TransactionComplete;
          })
          .catch((error) => {
            this.processing = false;
            this.submitted = false;
            alertService.showErrorAlert(error);
          });
      }
    },
    selectSubscriberType(type) {
      this.subscriberType = type;
    },
    selectSubscriberTypeWith(withType) {
      this.subscriberTypeWith = withType;
    },
  },
  watch: {
    currentStep() {
      // Clear errors on step change so ErrorBox doesn't persist across steps
      this.registrationError = "";
      this.submitted = false;
      this.processing = false;
      this.clearAlert();
    },
    password(value) {
      this.registrationError = ""; // Clear any previous errors when user starts typing
      this.passwordsMatch = this.confirmPassword === value;
    },
    confirmPassword(value) {
      this.registrationError = ""; // Clear any previous errors when user starts typing
      this.passwordsMatch = this.password === value;
    },
  },
};
</script>
